import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FloatingParticles } from './FloatingParticles';
type SectionProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
};
export function Section({
  children,
  className = '',
  id
}: SectionProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <motion.section ref={ref} id={id} initial={{
    y: 40
  }} animate={isInView ? {
    y: 0
  } : {
    y: 40
  }} transition={{
    duration: 0.8,
    ease: 'easeOut'
  }} className={`py-12 md:py-16 relative ${className}`} data-id="element-226">
      {/* Floating particles */}
      <FloatingParticles data-id="element-227" />

      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/65 pointer-events-none" data-id="element-228" />

      {/* Animated corner accents */}
      <motion.div className="absolute top-0 left-0 w-20 h-20 border-l-2 border-t-2 border-matrix-accent/30" initial={{
      opacity: 0,
      scale: 0
    }} animate={isInView ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 0
    }} transition={{
      duration: 0.6,
      delay: 0.2
    }} data-id="element-229" />
      <motion.div className="absolute top-0 right-0 w-20 h-20 border-r-2 border-t-2 border-matrix-accent/30" initial={{
      opacity: 0,
      scale: 0
    }} animate={isInView ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 0
    }} transition={{
      duration: 0.6,
      delay: 0.3
    }} data-id="element-230" />
      <motion.div className="absolute bottom-0 left-0 w-20 h-20 border-l-2 border-b-2 border-matrix-accent/30" initial={{
      opacity: 0,
      scale: 0
    }} animate={isInView ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 0
    }} transition={{
      duration: 0.6,
      delay: 0.4
    }} data-id="element-231" />
      <motion.div className="absolute bottom-0 right-0 w-20 h-20 border-r-2 border-b-2 border-matrix-accent/30" initial={{
      opacity: 0,
      scale: 0
    }} animate={isInView ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 0
    }} transition={{
      duration: 0.6,
      delay: 0.5
    }} data-id="element-232" />

      {/* Content with relative positioning */}
      <div className="relative z-10" data-id="element-233">{children}</div>
    </motion.section>;
}
type SectionTitleProps = {
  children: React.ReactNode;
  className?: string;
};
export function SectionTitle({
  children,
  className = ''
}: SectionTitleProps) {
  return <div className="text-center mb-8" data-id="element-234">
      <motion.h2 className={`text-2xl md:text-3xl font-black text-white mb-3 leading-tight uppercase tracking-wide ${className}`} initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} viewport={{
      once: true
    }} data-id="element-235">
        {children}
      </motion.h2>
      <motion.div className="accent-divider" initial={{
      scaleX: 0
    }} whileInView={{
      scaleX: 1
    }} transition={{
      duration: 0.8,
      delay: 0.2
    }} viewport={{
      once: true
    }} data-id="element-236" />
    </div>;
}
export function SectionSubtitle({
  children,
  className = ''
}: SectionTitleProps) {
  return <motion.h3 className={`text-xl md:text-2xl font-bold text-white mb-4 leading-snug ${className}`} initial={{
    opacity: 0,
    x: -20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.5
  }} viewport={{
    once: true
  }} data-id="element-237">
      {children}
    </motion.h3>;
}